import React, { useState } from 'react'
import { MediaGroupSection, MediaSection, TextSection } from '@mytours/components'
import type { MediaGroupItem, MediaSectionType, SectionHeadingPlacement } from '@mytours/components'
import {
	CollectionItem,
	ScreenSection,
	ScreenSectionItem,
	SCREEN_SECTION_TYPE_AUDIO,
	SCREEN_SECTION_TYPE_IMAGE,
	SCREEN_SECTION_TYPE_MEDIA_GROUP,
	SCREEN_SECTION_TYPE_SINGLE_MEDIA,
	SCREEN_SECTION_TYPE_TEXT,
} from 'mytours-read-common'

import type { MediaItem } from 'mytours-read-api-typescript'

import AudioPlayerTransport from '../../audio/components/v2/AudioPlayerTransport'
import FullscreenMedia from 'mobile_web/modules/screen/components/parts/FullscreenMedia'
import Modal from '../../common/components/modal'
import useScreenSize from '../../../common_lib/hooks/use-screen-size'
import { getDirMode } from '../../platform'
import { cleanText } from '../../common/functions'
import { ImageSizePreset, ScreenSize } from '../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../common_lib/utils'
import strings from '../../../strings'

interface Props {
	stop: CollectionItem
	collectionId: number
	// What the stop is called on the row above ("Stop 1: Welcome"), used to name the audio controls. The
	// row already spells this out; the panel repeats it rather than deriving it a second way.
	name: string
}

// The media a section holds, whichever way it holds it: a `media_group` carries a list, a `single_media`
// carries one item on `media_item`. An item with no file has nothing to draw and nothing to play, which
// is the same rule the pill over a collapsed thumbnail applies.
const sectionMedia = (section: ScreenSection): ScreenSectionItem[] => {
	const items = section.section_items ?? (section.media_item ? [section.media_item as ScreenSectionItem] : [])

	return items.filter(item => Boolean(item.file))
}

const imageOptions = { imageSizePreset: ImageSizePreset.Row }

// An unloaded poster has no intrinsic size, so it lays out at zero height and the panel jumps when the
// file arrives. tour.less turns this ratio into a reserved box; the Row preset never crops, so it holds.
const posterRatio = (item: ScreenSectionItem): React.CSSProperties | undefined =>
	item.file?.width && item.file.height
		? ({ '--poster-aspect-ratio': `${item.file.width} / ${item.file.height}` } as React.CSSProperties)
		: undefined

// StopRow's panel is the card's full width, because the transport draws a rule that has to meet both of
// its inner edges. Everything that is not the transport therefore pays the card's gutter itself, through
// the sections' own padding token rather than a wrapper — a wrapper would leave an element behind in a
// panel with nothing to show, and the row gives back its bottom padding only while it is `:empty`.
//
// The wide layout pays a deeper one. A collapsed row's thumbnail sits flush against the card's own
// gutter, but an open row's heading column starts further in, and the two are separate measures rather
// than one token used twice.
const SECTION_PADDING = '0 var(--sq-stop-row-padding-inline)'
const WIDE_SECTION_PADDING = '0 2.5rem'

// What an image is called, and nothing invented. `getImageAlt` — what the older screen renderer uses —
// falls back to the filename, which for an uploaded photograph is a hash: a screen reader reads out
// "1721876486_R52_9166-6b8bd182-4ea0912f.jpg", which is worse than silence. An image nobody described
// gets an empty alt instead, the same call StopRow already makes for the thumbnail beside the title.
const imageAlt = (item: ScreenSectionItem): string => cleanText(item.description || item.caption || '')

const mediaGroupItem = (item: ScreenSectionItem): MediaGroupItem => ({
	id: item.id,
	mediaType: SCREEN_SECTION_TYPE_IMAGE as MediaGroupItem['mediaType'],
	poster: generateImageUrl(item.file, imageOptions),
	posterSrcSet: generateImageSrcSet(item.file, imageOptions),
	posterAlt: imageAlt(item),
	// A media group names its control per tile rather than per section, which is the right grain: the
	// label belongs to the picture being opened. Every tile here opens the same way, so they share it.
	activateLabel: strings.accessibility_open_fullscreen_media,
})

/**
 * One stop's screen, as an expanded StopRow reveals it: every section the screen carries, in the order
 * the screen carries them, so a stop that leads with audio opens onto its player and a stop that leads
 * with prose opens onto that. Nothing here groups by type or reorders.
 *
 * The sections themselves are `@mytours/components`' — this only maps the API's shapes onto them:
 *
 *   text          → TextSection
 *   media_group   → MediaGroupSection, whose own `single` layout draws a one-image group as a plain
 *                   image and a longer one as a gallery. Split on the section type rather than on how
 *                   many items it happens to hold, so a section does not change component the day an
 *                   editor adds a second photograph.
 *   single_media  → MediaSection
 *   audio, wherever it sits → AudioPlayerTransport, which is the app's own and owns playback.
 *
 * A section with nothing in it renders nothing — no wrapper, no padding. That matters beyond tidiness:
 * StopRow's panel gives back its own bottom padding only while it is `:empty`, so an empty element here
 * would open a stop onto a strip of blank card.
 *
 * Video and the other media types are deliberately absent. They need players this panel does not have,
 * and a tile that does nothing when it is pressed is worse than one that is not drawn yet.
 */
// What the reader has open full screen: the section the picture came from, and which of that section's
// pictures was pressed. The index means nothing without the list it indexes, so the two travel together.
interface ZoomedMedia {
	section: ScreenSection
	images: ScreenSectionItem[]
	index: number
}

const StopScreen: React.FC<Props> = ({ stop, collectionId, name }) => {
	const dir = getDirMode()

	// Heading beside content is the wide layout, and the library leaves the choice here because a section
	// cannot know how much room it has been given. The panel is as wide as the page on this layout, so the
	// window is a fair reading of it — the same one the tour page already takes for its cover image.
	const headingPlacement: SectionHeadingPlacement = useScreenSize() === ScreenSize.lg ? 'beside' : 'above'
	const padding = headingPlacement === 'beside' ? WIDE_SECTION_PADDING : SECTION_PADDING

	const [zoomed, setZoomed] = useState<ZoomedMedia | null>(null)
	const [zoomPresented, setZoomPresented] = useState(false)

	const closeZoom = () => {
		setZoomed(null)
		setZoomPresented(false)
	}

	const renderSection = (section: ScreenSection): React.ReactNode => {
		if (section.hide) {
			return null
		}

		if (section.section_type === SCREEN_SECTION_TYPE_TEXT) {
			if (!section.body && !section.title) {
				return null
			}

			return (
				<TextSection
					headingPlacement={headingPlacement}
					heading={section.title ?? undefined}
					body={section.body ?? undefined}
					collapsable={Boolean(section.collapsable)}
					exposableContent={section.exposable_content}
					dir={dir}
					padding={padding}
					expandLabel={strings.expand}
					collapseLabel={strings.collapse}
					itemId={section.id}
				/>
			)
		}

		if (
			section.section_type !== SCREEN_SECTION_TYPE_MEDIA_GROUP &&
			section.section_type !== SCREEN_SECTION_TYPE_SINGLE_MEDIA
		) {
			return null
		}

		const media = sectionMedia(section)
		const images = media.filter(item => item.media_type === SCREEN_SECTION_TYPE_IMAGE)
		// Its own container per section, because that is what playback is keyed on — and the same one the
		// pill over the collapsed thumbnail uses, so the two controls stay one player rather than two.
		const container = { type: 'section' as const, id: section.id, title: section.title }

		const audio = media
			.filter(item => item.media_type === SCREEN_SECTION_TYPE_AUDIO)
			.map(item => (
				<AudioPlayerTransport
					key={item.id}
					audioPreview={item}
					container={container}
					collectionId={collectionId}
					screen={stop.screen}
					name={name}
				/>
			))

		if (images.length === 0) {
			return audio
		}

		const heading = section.title ?? undefined
		// `zoomable` is per section, and a section without it gets no control rather than one that does
		// nothing when it is pressed — the library draws the button only when it is handed a handler.
		const zoom = section.zoomable ? (index: number) => setZoomed({ section, images, index }) : undefined
		const visuals =
			section.section_type === SCREEN_SECTION_TYPE_SINGLE_MEDIA ? (
				<MediaSection
					mediaType={SCREEN_SECTION_TYPE_IMAGE as MediaSectionType}
					poster={generateImageUrl(images[0].file, imageOptions)}
					posterSrcSet={generateImageSrcSet(images[0].file, imageOptions)}
					posterAlt={imageAlt(images[0])}
					heading={heading}
					headingPlacement={headingPlacement}
					caption={images[0].caption ?? undefined}
					dir={dir}
					padding={padding}
					itemId={section.id}
					onActivate={zoom && (() => zoom(0))}
					activateLabel={strings.accessibility_open_fullscreen_media}
					style={posterRatio(images[0])}
				/>
			) : (
				<MediaGroupSection
					items={images.map(mediaGroupItem)}
					heading={heading}
					headingPlacement={headingPlacement}
					showDots={images.length > 1}
					dir={dir}
					padding={padding}
					itemId={section.id}
					onItemActivate={zoom}
					// Only the single layout draws a free-height poster; carousel tiles are already fixed-size.
					style={images.length === 1 ? posterRatio(images[0]) : undefined}
				/>
			)

		return (
			<>
				{visuals}
				{audio}
			</>
		)
	}

	return (
		<>
			{(stop.screen?.sections ?? []).map(section => (
				<React.Fragment key={section.id}>{renderSection(section)}</React.Fragment>
			))}
			{/* Mounted only while something is open. A panel with nothing in it has to stay genuinely empty:
			    StopRow gives back its own bottom padding while it is `:empty`, and a modal element sitting
			    there closed would open every stop with no screen onto a strip of blank card. */}
			{zoomed && (
				<Modal
					isOpen={true}
					cssClass="media-modal"
					showBackdrop={true}
					onDidPresent={() => setZoomPresented(true)}
					onDidDismiss={closeZoom}
				>
					{/* Built only once the modal is on screen: the carousel measures itself on mount, and one
					    built inside a modal that has not been presented measures nothing. */}
					{zoomPresented && (
						<FullscreenMedia
							collectionId={collectionId}
							mediaData={zoomed.images as unknown as MediaItem[]}
							activeSlideIndex={zoomed.index}
							onBack={closeZoom}
							section={zoomed.section}
							screen={stop.screen}
						/>
					)}
				</Modal>
			)}
		</>
	)
}

export default StopScreen
